import { useCallback, useRef, useState } from 'react';

import { makeStyles, styled } from '@material-ui/core';

import { Node, mergeAttributes } from '@tiptap/core';
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import classNames from 'classnames';

import {
  useBlockZoneId,
  useCardDelete,
  useCardFocusTracking,
  useIsEditorEditable,
} from '@spotify/confidence-editor-react';
import {
  blockIdAttribute,
  generateBlockId,
  getColorByClass,
} from '@spotify/confidence-editor-react';
import {
  tiptapBlockMarginBottom,
  tiptapBlockMarginTop,
} from '@spotify/confidence-editor-react';
import { CommentZone } from '@spotify/plugin-confidence-comments-react';

import { getColorClass, isMetricStale } from '../../metrics';
import { MetricCardToolbar } from '../MetricCardToolbar';
import { useReportBlockData } from '../ReportBlockDataContext';
import { useReportState } from '../ReportHeader/useReportState';
import { StaleMetricBadge } from '../StaleMetricBadge';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    metricCard: {
      setMetricCard: () => ReturnType;
    };
  }
}

const useMetricCardStyles = makeStyles(() => ({
  container: {
    width: '100%',
    marginBottom: tiptapBlockMarginBottom,
    marginTop: tiptapBlockMarginTop,
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: 'var(--background-highlight)',
    border: '1px solid transparent',
    borderRadius: 'var(--encore-corner-radius-larger-2)',
    padding: 'var(--encore-spacing-looser)',
    gap: 'var(--encore-spacing-tighter-2)',
    '&:hover': {
      borderColor: 'var(--decorative-subdued)',
    },
    '& $hoverToolbar': {
      opacity: 0,
      pointerEvents: 'none' as const,
    },
    '&:hover $hoverToolbar, &$cardFocused $hoverToolbar, &:focus-within $hoverToolbar':
      {
        opacity: 1,
        pointerEvents: 'auto' as const,
      },
  },
  hoverToolbar: {
    position: 'absolute' as const,
    top: 0,
    right: 0,
    transform: 'translateY(-100%)',
    paddingBottom: 6,
    zIndex: 1000,
  },
  cardFocused: {
    '&, &:hover': {
      borderColor: 'var(--essential-base)',
    },
  },
}));

const Label = styled('div')({
  fontSize: 'var(--encore-text-size-smaller-3)',
  color: 'var(--text-subdued)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
});

const Title = styled('div')({
  fontSize: 'var(--encore-text-size-base)',
  fontWeight: 600,
  color: 'var(--text-base)',
});

const Value = styled('div')((props: { colorClass: string }) => ({
  fontSize: '48px',
  fontWeight: 700,
  lineHeight: 1,
  color: getColorByClass(props.colorClass),
}));

const MetricRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingBottom: 'var(--encore-spacing-tighter-2)',
  borderBottom: '1px solid var(--decorative-subdued)',
});

const MetricLabel = styled('span')({
  fontSize: 'var(--encore-text-size-smaller-2)',
  color: 'var(--text-subdued)',
});

const MetricValue = styled('div')({
  fontSize: 'var(--encore-text-size-smaller-2)',
  color: 'var(--text-base)',
  textAlign: 'right',
});

const Conclusion = styled('div')((props: { colorClass: string }) => ({
  fontSize: 'var(--encore-text-size-smaller)',
  fontWeight: 700,
  color: getColorByClass(props.colorClass),
}));

const MetricCardComponent = ({
  node,
  updateAttributes,
  editor,
  getPos,
}: NodeViewProps) => {
  const classes = useMetricCardStyles();
  const isEditable = useIsEditorEditable(editor);
  const cardRef = useRef<HTMLDivElement>(null);
  const isFocused = useCardFocusTracking(editor, getPos, node.nodeSize);
  const handleDelete = useCardDelete(editor, getPos, node.nodeSize);
  const { availableMetrics } = useReportBlockData();
  const [isToolbarActive, setIsToolbarActive] = useState(false);
  const { isPublished } = useReportState();

  const showStaleIndicator = !isPublished;

  const handleSelect = useCallback(
    (metricId: string) => {
      const selectedMetric = availableMetrics?.find(m => m.id === metricId);
      if (!selectedMetric) return;

      updateAttributes({
        metricId: selectedMetric.id,
        label:
          selectedMetric.type === 'guardrail'
            ? 'Guardrail metric'
            : 'Success metric',
        title: selectedMetric.name,
        value: selectedMetric.value || '—',
        sampleSize: selectedMetric.sampleSize?.toLocaleString() || '—',
        lowerBound: selectedMetric.lowerBound || '—',
        upperBound: selectedMetric.upperBound || '—',
        conclusion: selectedMetric.conclusion || 'No data available',
        status: selectedMetric.status,
        metricType: selectedMetric.type,
        hasNoninferiorityMargin: selectedMetric.hasNoninferiorityMargin,
      });
    },
    [updateAttributes, availableMetrics],
  );

  const label = node.attrs.label || 'Guardrail metric';
  const title = node.attrs.title || 'Select a metric';
  const value = node.attrs.value || '—';
  const sampleSize = node.attrs.sampleSize || '—';
  const lowerBound = node.attrs.lowerBound || '—';
  const upperBound = node.attrs.upperBound || '—';
  const conclusion = node.attrs.conclusion || 'No data available';
  const metricStatus = node.attrs.status;
  const metricType = node.attrs.metricType as 'guardrail' | 'success';
  const hasNoninferiorityMargin = node.attrs.hasNoninferiorityMargin;

  const colorClass = getColorClass(
    metricStatus,
    metricType,
    hasNoninferiorityMargin,
  );

  const availableMetric = availableMetrics.find(
    m => m.id === node.attrs.metricId,
  );
  const isStale = availableMetric
    ? isMetricStale(
        {
          value,
          sampleSize,
          lowerBound,
          upperBound,
          status: metricStatus,
          type: metricType,
          hasNoninferiorityMargin,
        },
        availableMetric,
      )
    : false;

  const { blockId } = node.attrs;
  const zoneId = useBlockZoneId('metric-card', blockId, updateAttributes);

  const content = (
    <div className={classes.container}>
      <div
        ref={cardRef}
        className={classNames(classes.card, {
          [classes.cardFocused]: isFocused || isToolbarActive,
        })}
      >
        {isEditable && (
          <div contentEditable={false} className={classes.hoverToolbar}>
            <MetricCardToolbar
              editor={editor}
              metricId={node.attrs.metricId}
              metrics={availableMetrics}
              onSelect={handleSelect}
              onDelete={handleDelete}
              onToolbarActiveChange={setIsToolbarActive}
              zoneId={zoneId}
              getPos={getPos}
              isStale={showStaleIndicator && isStale}
            />
          </div>
        )}
        <Label>{label}</Label>
        <Title>{title}</Title>
        <Value colorClass={colorClass}>{value}</Value>
        <MetricRow>
          <MetricLabel>Sample size</MetricLabel>
          <MetricValue>{sampleSize}</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>Lower bound</MetricLabel>
          <MetricValue>{lowerBound}</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>Upper bound</MetricLabel>
          <MetricValue>{upperBound}</MetricValue>
        </MetricRow>
        <Conclusion colorClass={colorClass}>{conclusion}</Conclusion>

        {showStaleIndicator && isStale && <StaleMetricBadge />}
      </div>
    </div>
  );

  return (
    <NodeViewWrapper>
      {zoneId ? (
        <CommentZone id={zoneId} component="div">
          {content}
        </CommentZone>
      ) : (
        content
      )}
    </NodeViewWrapper>
  );
};

export const MetricCard = Node.create({
  name: 'metricCard',
  group: 'block',
  atom: true,
  defining: true,

  addAttributes() {
    return {
      metricId: {
        default: null,
        parseHTML: element => element.getAttribute('data-metric-id'),
        renderHTML: attributes => ({
          'data-metric-id': attributes.metricId,
        }),
      },
      label: {
        default: 'Select a metric',
        parseHTML: element => element.getAttribute('data-label'),
        renderHTML: attributes => ({
          'data-label': attributes.label,
        }),
      },
      title: {
        default: 'Select a metric',
        parseHTML: element => element.getAttribute('data-title'),
        renderHTML: attributes => ({
          'data-title': attributes.title,
        }),
      },
      value: {
        default: '—',
        parseHTML: element => element.getAttribute('data-value'),
        renderHTML: attributes => ({
          'data-value': attributes.value,
        }),
      },
      sampleSize: {
        default: '—',
        parseHTML: element => element.getAttribute('data-sample-size'),
        renderHTML: attributes => ({
          'data-sample-size': attributes.sampleSize,
        }),
      },
      lowerBound: {
        default: '-',
        parseHTML: element => element.getAttribute('data-lower-bound'),
        renderHTML: attributes => ({
          'data-lower-bound': attributes.lowerBound,
        }),
      },
      upperBound: {
        default: '—',
        parseHTML: element => element.getAttribute('data-upper-bound'),
        renderHTML: attributes => ({
          'data-upper-bound': attributes.upperBound,
        }),
      },
      conclusion: {
        default: 'No data available',
        parseHTML: element => element.getAttribute('data-conclusion'),
        renderHTML: attributes => ({
          'data-conclusion': attributes.conclusion,
        }),
      },
      status: {
        default: null,
        parseHTML: element => element.getAttribute('data-status'),
        renderHTML: attributes => ({
          'data-status': attributes.status,
        }),
      },
      metricType: {
        default: '-',
        parseHTML: element => element.getAttribute('data-metric-type'),
        renderHTML: attributes => ({
          'data-metric-type': attributes.metricType,
        }),
      },
      hasNoninferiorityMargin: {
        default: false,
        parseHTML: element =>
          element.getAttribute('data-has-noninferiority-margin') === 'true',
        renderHTML: attributes => ({
          'data-has-noninferiority-margin': String(
            attributes.hasNoninferiorityMargin,
          ),
        }),
      },
      blockId: blockIdAttribute,
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="metric-card"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'metric-card' }),
    ];
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(MetricCardComponent);
  },

  addCommands() {
    return {
      setMetricCard:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { blockId: generateBlockId() },
          });
        },
    };
  },
});
